import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { showToast } from './Toast';
import { useCountriesQuery, useConsortiumsQuery, useAgenciesQuery, useSubAgenciesQuery } from '../../src/slices/api';
import LoginModal from './LoginModal';
import ClaimWelcomeModal from './ClaimWelcomeModal';
import ClaimThankYou from './ClaimThankYou';
import SignUpService from '../../services/SignUpService';
import CheckUserService from '../../services/CheckUserService';
import SignInService from '../../services/SignInService';
import MobileVerifyModal from './MobileVerifyModal';
import FormField from './FormField';
import AccountInfoFields from './TravelAdvisorForm/AccountInfoFields';
import AccountInfoReadOnly from './TravelAdvisorForm/AccountInfoReadOnly';
import AdvisorInfoFields from './TravelAdvisorForm/AdvisorInfoFields';
import AdvisorInfoReadOnly from './TravelAdvisorForm/AdvisorInfoReadOnly';
import TermsAndSubmit from './TravelAdvisorForm/TermsAndSubmit';
import ReferralSection from './TravelAdvisorForm/ReferralSection';
import RegistrationComplete from './TravelAdvisorForm/RegistrationComplete';

const OTHER_VALUE = '__OTHER__';

const TravelAdvisorSignUpForm = ({
  onSubmit,
  className = '',
  showLoginLink = true,
  onLoginSuccess,
  htmlId,
  ctaButtonText,
  ctaButtonLink
}) => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Get brandId from window.gon or default to 'shipsticks'
  const brandId = window.gon?.current_brand || 'shipsticks';

  // Check if user is signed in (auth_token is present when logged in)
  const [isUserSignedIn, setIsUserSignedIn] = useState(!!window.gon?.auth_token);

  // Check if user is already a travel agent
  const [isTravelAgent, setIsTravelAgent] = useState(() => {
    const userRole = window.user_data?.user_role || '';
    return userRole.includes('travel_agent');
  });

  // Pre-populate form data from existing account when logged in
  const [formData, setFormData] = useState({
    // Step 1 - Account information
    email: isUserSignedIn ? (window.user_data?.user_email || '') : '',
    firstName: isUserSignedIn ? (window.user_data?.user_first_name || '') : '',
    lastName: isUserSignedIn ? (window.user_data?.user_last_name || '') : '',
    country: isUserSignedIn ? (window.user_data?.country_code || '') : '',
    mobilePhone: isUserSignedIn ? (window.user_data?.phone || '') : '',
    password: '',
    confirmPassword: '',
    // Step 2 - Travel advisor information
    consortium: '',
    agency: '',
    subAgency: '',
    travelAdvisorId: '',
    // "Other" free-text names
    otherConsortiumName: '',
    otherAgencyName: '',
    otherSubAgencyName: '',
    agreedToTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [isExistingAdvisor, setIsExistingAdvisor] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState(null);
  const [isMobileVerifyOpen, setIsMobileVerifyOpen] = useState(false);
  const [verifyAuthToken, setVerifyAuthToken] = useState(null);
  const submittingRef = useRef(false);
  const formStartedTracked = useRef(false);

  const trackFormStarted = useCallback(() => {
    if (formStartedTracked.current) return;
    formStartedTracked.current = true;

    try {
      window.rudderanalytics?.track('ta_form_started', { brand: brandId });
    } catch (e) {
      // silently ignore tracking failures
    }
  }, [brandId]);

  const validatePassword = (value) => {
    if (!value) return null;
    if (value.length < 6) return 'Password must be at least 6 characters.';
    return null;
  };

  const validateConfirmPassword = (value, password) => {
    if (!value) return null;
    if (value !== password) return 'Passwords do not match.';
    return null;
  };

  // Handle successful login from LoginModal — update globals and re-render as logged-in
  const handleLoginSuccess = useCallback((data) => {
    // Update window globals so other code that reads them stays consistent
    if (!window.gon) window.gon = {};
    window.gon.auth_token = data.auth_token || data.authToken;

    if (!window.user_data) window.user_data = {};
    window.user_data.user_email = data.email;
    window.user_data.user_first_name = data.first_name;
    window.user_data.user_last_name = data.last_name;
    window.user_data.phone = data.phone_number;
    window.user_data.user_role = Array.isArray(data.roles) ? data.roles.join(',') : (data.roles || '');
    window.user_data.travel_advisor_display_id = data.travel_advisor_display_id || null;
    if (data.country_of_residence) {
      window.user_data.country_of_residence = data.country_of_residence;
    }
    if (data.country_code) {
      window.user_data.country_code = data.country_code;
    }

    // Pre-populate form fields with logged-in user data
    setFormData(prev => ({
      ...prev,
      email: data.email || prev.email,
      firstName: data.first_name || prev.firstName,
      lastName: data.last_name || prev.lastName,
      mobilePhone: data.phone_number || prev.mobilePhone,
      country: data.country_code || prev.country
    }));

    // Check if already a travel agent — show referral link immediately
    const roles = window.user_data.user_role || '';
    const displayId = data.travel_advisor_display_id;
    if (roles.includes('travel_agent')) {
      setIsTravelAgent(true);
      if (displayId) {
        setRegistrationComplete(true);
        setReferralLink(`${window.location.origin}/travel?agent_id=${displayId}`);
      }
    }

    // Fetch advisor data by display_id or by email as fallback
    const fetchAdvisorData = async () => {
      try {
        let advisorData;
        if (displayId) {
          const params = new URLSearchParams({ display_id: displayId });
          const response = await fetch(`/api/v6/travel_agent/advisors/search?${params}`);
          if (response.ok) {
            const json = await response.json();
            advisorData = json.data || json;
          }
        } else if (data.email) {
          const params = new URLSearchParams({ email: data.email });
          const response = await fetch(`/api/v6/travel_agent/advisors/search?${params}`);
          if (response.ok) {
            const json = await response.json();
            advisorData = json.data || json;
          }
        }

        if (advisorData?.found) {
          const fetchedDisplayId = advisorData.display_id || displayId;
          const consortiumId = advisorData.consortium_id || '';
          const agencyId = advisorData.parent_agency_id || advisorData.agency_id || '';
          const subAgencyId = advisorData.parent_agency_id ? advisorData.agency_id : '';

          setIsExistingAdvisor(true);
          setFormData(prev => ({
            ...prev,
            consortium: consortiumId,
            agency: agencyId,
            subAgency: subAgencyId,
            travelAdvisorId: advisorData.travel_advisor_id || ''
          }));
          const hasSubAgency = !!advisorData.parent_agency_id;
          setClaimData(prev => ({
            ...prev,
            consortium_name: advisorData.consortium_name || '',
            agency_name: hasSubAgency ? '' : (advisorData.agency_name || ''),
            sub_agency_name: hasSubAgency ? (advisorData.agency_name || '') : ''
          }));

          // Show referral link if we found the advisor (even without displayId from login response)
          if (fetchedDisplayId) {
            setReferralLink(`${window.location.origin}/travel?agent_id=${fetchedDisplayId}`);
            if (roles.includes('travel_agent')) {
              setRegistrationComplete(true);
            }
          }
        }
      } catch (err) {
        console.error('Advisor data fetch error:', err);
      }
    };
    fetchAdvisorData();

    // Trigger re-render into the logged-in view
    setIsUserSignedIn(true);

    // After the page settles, fetch advisor data and set the referral link.
    // We wait 500ms so the component is fully mounted in its logged-in state
    // before making async state updates (avoids "update on unmounted component").
    const email = data.email;
    setTimeout(async () => {
      try {
        const searchParam = displayId
          ? new URLSearchParams({ display_id: displayId })
          : email
            ? new URLSearchParams({ email, lookup_by_user: 'true' })
            : null;
        if (!searchParam) return;

        const response = await fetch(`/api/v6/travel_agent/advisors/search?${searchParam}`);
        if (!response.ok) return;
        const json = await response.json();
        const advisorData = json.data || json;
        if (advisorData.found) {
          if (advisorData.display_id) {
            setReferralLink(`${window.location.origin}/travel?agent_id=${advisorData.display_id}`);
          } else if (advisorData.referral_url) {
            const url = advisorData.referral_url;
            setReferralLink(url.startsWith('http') ? url : `${window.location.origin}${url}`);
          }
        }
      } catch (err) {
        console.error('Referral link fetch error:', err);
      }
    }, 500);

    // Call parent callback if provided
    if (onLoginSuccess) {
      onLoginSuccess(data);
    }
  }, [onLoginSuccess]);

  // Claim flow state
  const [claimData, setClaimData] = useState(null);
  const [isClaimFlow, setIsClaimFlow] = useState(false);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [isClaimReadOnly, setIsClaimReadOnly] = useState(true);
  const [isClaimComplete, setIsClaimComplete] = useState(false);
  const [isClaimLoading, setIsClaimLoading] = useState(false);

  // Fetch countries from API
  const {
    isLoading: isLoadingCountries,
    data: countriesData
  } = useCountriesQuery(brandId);

  // Fetch consortiums from API
  const {
    isLoading: isLoadingConsortiums,
    data: consortiumsData
  } = useConsortiumsQuery();

  // Fetch agencies when consortium is selected
  const {
    isLoading: isLoadingAgenciesQuery,
    data: agenciesData
  } = useAgenciesQuery(formData.consortium, {
    skip: !formData.consortium || formData.consortium === OTHER_VALUE
  });

  // Fetch sub-agencies when agency is selected
  const {
    isLoading: isLoadingSubAgenciesQuery,
    data: subAgenciesData
  } = useSubAgenciesQuery(formData.agency, {
    skip: !formData.agency || formData.agency === OTHER_VALUE
  });

  // Derived state — recomputed when data changes (no useState/useEffect needed)
  const countryOptions = useMemo(() => {
    if (!countriesData || !Array.isArray(countriesData)) return [];
    const uniqueCountries = countriesData.reduce((acc, country) => {
      if (!acc.find(c => c.code === country.code)) {
        acc.push(country);
      }
      return acc;
    }, []);
    const usa = uniqueCountries.find(c => c.code === 'US');
    const rest = uniqueCountries.filter(c => c.code !== 'US');
    const ordered = usa ? [usa, ...rest] : rest;
    return ordered.map((country) => ({
      value: country.code,
      label: country.name
    }));
  }, [countriesData]);

  const consortiumOptions = useMemo(() => {
    if (!consortiumsData || !Array.isArray(consortiumsData)) return [];
    return consortiumsData.map((consortium) => ({
      value: consortium.id,
      label: consortium.name
    }));
  }, [consortiumsData]);

  const agencyOptions = useMemo(() => {
    if (formData.consortium === OTHER_VALUE) return [];
    if (agenciesData && Array.isArray(agenciesData)) {
      return agenciesData.map((agency) => ({
        value: agency.id,
        label: agency.name
      }));
    }
    return [];
  }, [agenciesData, formData.consortium]);

  const isLoadingAgencies = formData.consortium !== OTHER_VALUE && isLoadingAgenciesQuery;

  const subAgencyOptions = useMemo(() => {
    if (formData.agency === OTHER_VALUE) return [];
    if (subAgenciesData && Array.isArray(subAgenciesData)) {
      return subAgenciesData.map((subAgency) => ({
        value: subAgency.id,
        label: subAgency.name
      }));
    }
    return [];
  }, [subAgenciesData, formData.agency]);

  const isLoadingSubAgencies = formData.agency !== OTHER_VALUE && isLoadingSubAgenciesQuery;

  // Lock consortium/agency/sub-agency fields when claiming via agent_id
  const hasAgentIdParam = new URLSearchParams(window.location.search).has('agent_id')
    || new URLSearchParams(window.location.search).has('advisor_id');

  // Detect Signature Travel Network consortium (check options list and claimData fallback)
  const isSignatureConsortium = useMemo(() => {
    const selectedConsortium = consortiumOptions.find(o => o.value === formData.consortium);
    const consortiumName = selectedConsortium?.label || claimData?.consortium_name || '';
    return consortiumName.toLowerCase() === 'signature travel network';
  }, [consortiumOptions, formData.consortium, claimData]);

  // On mount: if user is already signed in, fetch their advisor data to populate
  // consortium/agency/sub-agency and set isExistingAdvisor correctly.
  useEffect(() => {
    if (!isUserSignedIn) return;

    const fetchAdvisorData = async () => {
      try {
        // Try display_id first, fall back to email lookup
        const displayId = window.user_data?.travel_advisor_display_id
          || window.gon?.travel_advisor_display_id;
        const email = window.user_data?.user_email;

        let searchParams;
        if (displayId) {
          searchParams = new URLSearchParams({ display_id: displayId });
        } else if (email) {
          searchParams = new URLSearchParams({ email: email, lookup_by_user: 'true' });
        } else {
          return;
        }

        const response = await fetch(`/api/v6/travel_agent/advisors/search?${searchParams}`);
        if (!response.ok) return;
        const json = await response.json();
        const advisorData = json.data || json;
        if (advisorData.found) {
          setIsExistingAdvisor(true);
          if (advisorData.display_id) {
            setReferralLink(`${window.location.origin}/travel?agent_id=${advisorData.display_id}`);
          } else if (advisorData.referral_url) {
            const url = advisorData.referral_url;
            setReferralLink(url.startsWith('http') ? url : `${window.location.origin}${url}`);
          }

          const consortiumId = advisorData.consortium_id || '';
          const agencyId = advisorData.parent_agency_id || advisorData.agency_id || '';
          const subAgencyId = advisorData.parent_agency_id ? advisorData.agency_id : '';

          setFormData(prev => ({
            ...prev,
            email: advisorData.advisor_email || prev.email,
            firstName: advisorData.first_name || prev.firstName,
            lastName: advisorData.last_name || prev.lastName,
            consortium: consortiumId,
            agency: agencyId,
            subAgency: subAgencyId,
            travelAdvisorId: advisorData.travel_advisor_id || ''
          }));
          // Store names for read-only display fallback while option lists load.
          // When the advisor is in a sub-agency, agency_name is the sub-agency name
          // and we need to resolve the parent agency name separately.
          const hasSubAgency = !!advisorData.parent_agency_id;
          setClaimData(prev => ({
            ...prev,
            consortium_name: advisorData.consortium_name || '',
            agency_name: hasSubAgency ? '' : (advisorData.agency_name || ''),
            sub_agency_name: hasSubAgency ? (advisorData.agency_name || '') : ''
          }));
        }
      } catch (err) {
        console.error('Advisor data fetch error:', err);
      }
    };
    fetchAdvisorData();
  }, []);

  // Check URL for claim link parameters on mount
  // Supports both agent_id (API lookup) and direct URL params (pre-population)
  useEffect(() => {
    if (isUserSignedIn) return;

    const urlParams = new URLSearchParams(window.location.search);
    const agentId = urlParams.get('agent_id') || urlParams.get('advisor_id');

    // Extract direct URL parameters for pre-population
    const urlFirstName = urlParams.get('first_name') || urlParams.get('ta_first_name');
    const urlLastName = urlParams.get('last_name') || urlParams.get('ta_last_name');
    const urlEmail = urlParams.get('email') || urlParams.get('ta_email');
    const urlPhone = urlParams.get('phone') || urlParams.get('ta_phone');
    const urlCountry = urlParams.get('country') || urlParams.get('ta_country');
    const urlConsortiumId = urlParams.get('consortium_id');
    const urlAgencyId = urlParams.get('agency_id');
    const urlSubAgencyId = urlParams.get('sub_agency_id');

    // If we have direct URL params but no agent_id, pre-populate form without API lookup
    const hasDirectParams = urlFirstName || urlLastName || urlEmail || urlPhone ||
                           urlCountry || urlConsortiumId || urlAgencyId || urlSubAgencyId;

    if (hasDirectParams && !agentId) {
      // Pre-populate form from URL parameters
      setFormData(prev => ({
        ...prev,
        email: urlEmail || prev.email,
        firstName: urlFirstName || prev.firstName,
        lastName: urlLastName || prev.lastName,
        mobilePhone: urlPhone || prev.mobilePhone,
        country: urlCountry || prev.country,
        consortium: urlConsortiumId || prev.consortium,
        agency: urlAgencyId || prev.agency,
        subAgency: urlSubAgencyId || prev.subAgency
      }));

      // Show claim flow UI if we have identifying params (name + email)
      if (urlFirstName && urlEmail) {
        setIsClaimFlow(true);
        setIsClaimModalOpen(true);
        setClaimData({
          first_name: urlFirstName,
          last_name: urlLastName,
          advisor_email: urlEmail,
          phone: urlPhone,
          consortium_id: urlConsortiumId,
          agency_id: urlAgencyId
        });
      }
      return;
    }

    if (!agentId) return;

    const fetchClaimData = async () => {
      setIsClaimLoading(true);
      try {
        const params = new URLSearchParams({
          display_id: agentId
        });
        const response = await fetch(
          `/api/v6/travel_agent/advisors/search?${params}`
        );
        if (!response.ok) return;

        const json = await response.json();
        const data = json.data || json;

        if (data.found) {
          // Map agency hierarchy to form dropdowns
          const consortiumId = data.consortium_id || '';
          const agencyId = data.parent_agency_id || data.agency_id || '';
          const subAgencyId = data.parent_agency_id ? data.agency_id : '';

          // Pre-populate form for ANY found advisor (imported or existing)
          // API data takes precedence for security to prevent phishing attacks
          setFormData(prev => ({
            ...prev,
            email: data.advisor_email ?? urlEmail ?? '',
            firstName: data.first_name ?? urlFirstName ?? '',
            lastName: data.last_name ?? urlLastName ?? '',
            mobilePhone: data.phone ?? urlPhone ?? '',
            country: urlCountry || prev.country,
            consortium: consortiumId ?? urlConsortiumId ?? '',
            agency: agencyId ?? urlAgencyId ?? '',
            subAgency: subAgencyId ?? urlSubAgencyId ?? '',
            travelAdvisorId: data.travel_advisor_id ?? ''
          }));

          if (data.scenario === 'imported_user') {
            // Imported user without account - show claim flow to create password
            setClaimData(data);
            setIsClaimFlow(true);
            setIsClaimModalOpen(true);
          } else if (data.scenario === 'existing_user') {
            // User already has an account - prompt them to log in
            setClaimData(data);
            setIsLoginModalOpen(true);
          }
        }
      } catch (err) {
        console.error('Claim flow fetch error:', err);
      } finally {
        setIsClaimLoading(false);
      }
    };

    fetchClaimData();
  }, []);

  // Search for existing advisor after agency fields are selected
  const searchForAdvisor = useCallback(async (email, consortiumId, agencyId) => {
    if (!email || !consortiumId || !agencyId) return;
    try {
      const params = new URLSearchParams({
        email,
        consortium_id: consortiumId,
        agency_id: agencyId
      });
      const response = await fetch(
        `/api/v6/travel_agent/advisors/search?${params}`
      );
      if (!response.ok) return;
      const json = await response.json();
      const data = json.data || json;
      if (data.found && data.travel_advisor_id) {
        setFormData(prev => ({
          ...prev, travelAdvisorId: data.travel_advisor_id
        }));
      } else {
        // GSE-11321: Don't clear travelAdvisorId if already set from claim flow
        setFormData(prev => ({ ...prev, travelAdvisorId: prev.travelAdvisorId || '' }));
      }
    } catch (err) {
      console.error('Advisor search error:', err);
      // GSE-11321: Don't clear travelAdvisorId if already set from claim flow
      setFormData(prev => ({ ...prev, travelAdvisorId: prev.travelAdvisorId || '' }));
    }
  }, []);

  const handleInputChange = useCallback((field, value) => {
    if (field === 'consortium') {
      // Clear agency, subAgency, advisor, and "Other" names when consortium changes
      // GSE-11321: Preserve travelAdvisorId during claim flow to prevent duplicate creation
      setFormData(prev => ({
        ...prev,
        [field]: value,
        agency: value === OTHER_VALUE ? OTHER_VALUE : '',
        subAgency: '',
        travelAdvisorId: isClaimFlow ? prev.travelAdvisorId : '',
        otherConsortiumName: value === OTHER_VALUE ? prev.otherConsortiumName : '',
        otherAgencyName: '',
        otherSubAgencyName: ''
      }));
      // Keep claimData in sync so the read-only view shows the correct name immediately
      const consortiumLabel = consortiumOptions.find(o => o.value === value)?.label;
      if (consortiumLabel) {
        setClaimData(prev => ({ ...prev, consortium_name: consortiumLabel, agency_name: '', sub_agency_name: '' }));
      }
    } else if (field === 'agency') {
      // Clear subAgency, advisor, and "Other" names when agency changes
      // GSE-11321: Preserve travelAdvisorId during claim flow to prevent duplicate creation
      setFormData(prev => ({
        ...prev,
        [field]: value,
        subAgency: '',
        travelAdvisorId: isClaimFlow ? prev.travelAdvisorId : '',
        otherAgencyName: value === OTHER_VALUE ? prev.otherAgencyName : '',
        otherSubAgencyName: ''
      }));
      // Keep claimData in sync so the read-only view shows the correct name immediately
      const agencyLabel = agencyOptions.find(o => o.value === value)?.label;
      if (agencyLabel) {
        setClaimData(prev => ({ ...prev, agency_name: agencyLabel, sub_agency_name: '' }));
      }
    } else if (field === 'subAgency') {
      setFormData(prev => {
        // Search for advisor when sub-agency is selected (skip for "Other")
        // Read email/consortium from prev to avoid stale closure
        if (value && value !== OTHER_VALUE && prev.email
            && prev.consortium && prev.consortium !== OTHER_VALUE) {
          searchForAdvisor(prev.email, prev.consortium, value);
        }
        return { ...prev, [field]: value };
      });
      // Keep claimData in sync so the read-only view shows the correct name immediately
      const subAgencyLabel = subAgencyOptions.find(o => o.value === value)?.label;
      if (subAgencyLabel) {
        setClaimData(prev => ({ ...prev, sub_agency_name: subAgencyLabel }));
      }
    } else if (field === 'otherSubAgencyName') {
      // When typing a sub-agency name, keep subAgency as OTHER_VALUE (user already selected "Other")
      setFormData(prev => ({
        ...prev,
        otherSubAgencyName: value
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  }, [searchForAdvisor, consortiumOptions, agencyOptions, subAgencyOptions]);

  const validateForm = useCallback(() => {
    // Account fields only required for new users (not logged in)
    if (!isUserSignedIn) {
      if (!formData.email) {
        showToast('Please enter your email address.', 'error');
        return false;
      }
      if (!formData.firstName) {
        showToast('Please enter your first name.', 'error');
        return false;
      }
      if (!formData.lastName) {
        showToast('Please enter your last name.', 'error');
        return false;
      }
      if (!isClaimFlow || !isClaimReadOnly) {
        if (!formData.country) {
          showToast('Please select your country of residence.', 'error');
          return false;
        }
        if (!formData.mobilePhone) {
          showToast('Please enter your mobile phone number.', 'error');
          return false;
        }
      }
      if (!formData.password) {
        showToast('Please enter a password.', 'error');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        showToast('Passwords do not match.', 'error');
        return false;
      }
    }
    if (!formData.consortium) {
      showToast('Please select a consortium.', 'error');
      return false;
    }
    if (formData.consortium === OTHER_VALUE && !formData.otherConsortiumName.trim()) {
      showToast('Please enter a consortium name.', 'error');
      return false;
    }
    if (!formData.agency) {
      showToast('Please select an agency.', 'error');
      return false;
    }
    if (formData.agency === OTHER_VALUE && !formData.otherAgencyName.trim()) {
      showToast('Please enter an agency name.', 'error');
      return false;
    }
    // Sub-agency is optional — do not block submission when none is selected
    // Sub-agency name is optional — if "Other" selected but name blank, no sub-agency created
    if (!formData.agreedToTerms) {
      showToast('Please agree to the Terms of Use and Privacy Policy.', 'error');
      return false;
    }
    return true;
  }, [formData, isUserSignedIn, isClaimFlow, isClaimReadOnly, subAgenciesData]);

  const checkEmailExists = useCallback(async () => {
    if (!formData.email) return;
    try {
      const result = await CheckUserService.checkUser(formData.email);
      if (result.messages === 'User Exist') {
        showToast(
          'An account with this email already exists. Please log in.',
          'error'
        );
        setIsLoginModalOpen(true);
      }
    } catch (error) {
      console.error('Check user error:', error);
    }
  }, [formData.email]);

  const handleSubmitExistingUser = useCallback(async () => {
    // For logged-in users: register as advisor and update user info via advisors API
    const authToken = window.gon?.auth_token;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const headers = { 'Content-Type': 'application/json' };
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
    if (authToken) {
      headers['x-user-token'] = authToken;
    }

    const sanitizeId = (val) => (val === OTHER_VALUE ? '' : val);
    const payload = {
      email: formData.email,
      first_name: formData.firstName,
      last_name: formData.lastName,
      phone: formData.mobilePhone,
      consortium_id: sanitizeId(formData.consortium),
      agency_id: sanitizeId(formData.agency),
      sub_agency_id: sanitizeId(formData.subAgency),
      travel_advisor_id: formData.travelAdvisorId || undefined
    };

    // Include "Other" custom names when applicable
    if (formData.consortium === OTHER_VALUE) {
      payload.other_consortium_name = formData.otherConsortiumName.trim();
    }
    if (formData.agency === OTHER_VALUE) {
      payload.other_agency_name = formData.otherAgencyName.trim();
    }
    if (formData.subAgency === OTHER_VALUE) {
      payload.other_sub_agency_name = formData.otherSubAgencyName.trim();
    }

    const response = await fetch('/api/v6/travel_agent/advisors/register', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (!response.ok) {
      const errorMsg = data.errors ? data.errors.join(', ') : (data.error || 'Registration failed');
      throw new Error(errorMsg);
    }

    return data;
  }, [formData]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (submittingRef.current) return;
    submittingRef.current = true;

    if (!validateForm()) {
      submittingRef.current = false;
      return;
    }

    setLoading(true);
    try {
      if (isUserSignedIn) {
        // Logged-in flow: register as advisor with existing account
        const data = await handleSubmitExistingUser();
        showToast(isExistingAdvisor ? 'Update successful!' : 'Registration successful!', 'success');

        if (isExistingAdvisor) {
          // Profile update: sync formData from API response then return to read-only view
          const advisorData = data.data || data;
          if (advisorData) {
            const hasSubAgency = !!advisorData.parent_agency_id;
            const agencyId = advisorData.parent_agency_id || advisorData.agency_id || '';
            const subAgencyId = hasSubAgency ? advisorData.agency_id : '';
            setFormData(prev => ({
              ...prev,
              consortium: advisorData.consortium_id || prev.consortium,
              agency: agencyId,
              subAgency: subAgencyId
            }));
            setClaimData(prev => ({
              ...prev,
              consortium_name: advisorData.consortium_name || '',
              agency_name: hasSubAgency ? '' : (advisorData.agency_name || ''),
              sub_agency_name: hasSubAgency ? (advisorData.agency_name || '') : ''
            }));
          }
          setIsEditMode(false);
        } else {
          // New registration: show referral link screen
          const advisorData = data.data || data;
          if (advisorData.referral_url) {
            const url = advisorData.referral_url;
            setReferralLink(url.startsWith('http') ? url : `${window.location.origin}${url}`);
          }
          setRegistrationComplete(true);
        }

        if (onSubmit) {
          await onSubmit(data);
        }
      } else {
        // New user flow: check email, then create account
        const result = await CheckUserService.checkUser(formData.email);
        if (result.messages === 'User Exist') {
          showToast(
            'An account with this email already exists. Please log in.',
            'error'
          );
          setIsLoginModalOpen(true);
          return;
        }

        // Search for existing advisor before creating user (skip when "Other" selected)
        let submitData = { ...formData };
        const agencyId = formData.subAgency || formData.agency;
        const hasOther = [formData.consortium, formData.agency, formData.subAgency]
          .some(v => v === OTHER_VALUE);
        if (agencyId && !hasOther && formData.consortium && formData.email) {
          try {
            const params = new URLSearchParams({
              email: formData.email,
              consortium_id: formData.consortium,
              agency_id: agencyId
            });
            const searchResp = await fetch(
              `/api/v6/travel_agent/advisors/search?${params}`
            );
            if (searchResp.ok) {
              const searchJson = await searchResp.json();
              const searchData = searchJson.data || searchJson;
              if (searchData.found && searchData.travel_advisor_id) {
                submitData.travelAdvisorId = searchData.travel_advisor_id;
              }
            }
          } catch (err) {
            console.error('Advisor search error:', err);
          }
        }

        const data = await SignUpService.createUser(submitData);

        if (data.success) {
          // Update auth state so the page reflects logged-in status
          if (data.auth_token) {
            if (!window.gon) window.gon = {};
            window.gon.auth_token = data.auth_token;
            localStorage.setItem('auth_token', data.auth_token);

            if (!window.user_data) window.user_data = {};
            window.user_data.user_email = data.email;
            window.user_data.user_first_name = data.first_name;
            window.user_data.user_last_name = data.last_name;
            window.user_data.phone = data.phone_number;
            window.user_data.country_code = data.country_code;
            window.user_data.user_role = Array.isArray(data.roles) ? data.roles.join(',') : (data.roles || '');

            setIsUserSignedIn(true);
            window.dispatchEvent(new Event('auth-login'));
          }

          showToast('Registration successful!', 'success');

          // Build referral link from response (needed for both claim and normal flow)
          if (data.referral_url) {
            setReferralLink(`${window.location.origin}/travel?agent_id=${data.travel_advisor_display_id}`);
          }

          // Show mobile verify for both claim and net-new flows
          if (data.auth_token) {
            // Use auth_token from signup response directly
            setVerifyAuthToken(data.auth_token);
            setIsMobileVerifyOpen(true);
          } else {
            // Fall back to signing in to get auth token for mobile verify
            try {
              const signInData = await SignInService.signIn(formData.email, formData.password);
              if (signInData.auth_token) {
                setVerifyAuthToken(signInData.auth_token);
                if (!window.gon) window.gon = {};
                window.gon.auth_token = signInData.auth_token;
                setIsMobileVerifyOpen(true);
              } else {
                // Sign-in didn't return token — skip verify, show completion
                if (isClaimFlow) {
                  setIsClaimComplete(true);
                } else {
                  setRegistrationComplete(true);
                }
              }
            } catch (signInErr) {
              console.error('Auto sign-in for mobile verify failed:', signInErr);
              if (isClaimFlow) {
                setIsClaimComplete(true);
              } else {
                setRegistrationComplete(true);
              }
            }
          }

          if (data.referral_url) {
            const url = data.referral_url;
            setReferralLink(url.startsWith('http') ? url : `${window.location.origin}${url}`);
          }

          if (onSubmit) {
            await onSubmit(data);
          }
        } else {
          const errorMessages = data.messages
            ? Object.values(data.messages).flat().join(', ')
            : 'Registration failed';
          throw new Error(errorMessages);
        }
      }
    } catch (error) {
      const message = error.response?.data?.messages
        ? Object.values(error.response.data.messages).flat().join(', ')
        : error.message || 'An error occurred. Please try again.';
      showToast(message, 'error');
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  }, [validateForm, isUserSignedIn, handleSubmitExistingUser, formData, isClaimFlow, onSubmit]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleMobileVerifyComplete = useCallback(() => {
    setIsMobileVerifyOpen(false);
    if (isClaimFlow) {
      setIsClaimComplete(true);
    } else {
      setRegistrationComplete(true);
    }
  }, [isClaimFlow]);

  // Shared props for advisor info components
  const advisorFieldProps = {
    formData,
    handleInputChange,
    loading,
    consortiumOptions,
    agencyOptions,
    subAgencyOptions,
    isLoadingConsortiums,
    isLoadingAgencies,
    isLoadingSubAgencies,
    hideAgencyManualEntry: isSignatureConsortium,
    lockAgencyFields: isSignatureConsortium && (hasAgentIdParam || (isEditMode && isExistingAdvisor))
  };

  const advisorReadOnlyProps = {
    formData,
    consortiumOptions,
    agencyOptions,
    subAgencyOptions,
    claimData
  };

  // Claim flow: loading state while fetching advisor data
  if (isClaimLoading) {
    return (
      <div id={htmlId} data-testid="ta-registration-form" className={`max-w-[1440px] mx-auto px-8 py-12 ${className}`}>
        <p className="text-[18px] font-normal leading-[23px] text-black"
          style={{ fontFamily: 'Poppins, sans-serif' }}>
          Loading...
        </p>
      </div>
    );
  }

  // Claim flow: Thank You view after successful registration
  if (isClaimComplete) {
    return (
      <ClaimThankYou
        referralUrl={claimData?.referral_url}
        className={className}
        htmlId={htmlId}
        ctaButtonText={ctaButtonText}
        ctaButtonLink={ctaButtonLink}
      />
    );
  }

  // Already a travel agent — show confirmation message (unless claim flow is active)
  if (isUserSignedIn && isTravelAgent && !isClaimFlow) {
    return (
      <div id={htmlId} data-testid="ta-registration-form" className={`max-w-[1440px] mx-auto px-8 py-12 ${className}`}>
        <div
          className="flex items-center gap-3 bg-blue-50 px-6 py-4 rounded-lg max-w-[1152px]"
          data-testid="ta-already-registered"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
            <circle cx="12" cy="12" r="9.5" stroke="#252525" strokeWidth="1.5"/>
            <path d="M12 11V16M12 8H12.01" stroke="#252525" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span className="text-[18px] font-normal leading-[23px] text-black"
            style={{ fontFamily: 'Poppins, sans-serif' }}>
            You are already registered as a travel advisor.
          </span>
        </div>
        {referralLink && (
          <div className="mt-6 max-w-[1152px]">
            <ReferralSection
              referralLink={referralLink}
              copySuccess={copySuccess}
              onCopyLink={handleCopyLink}
            />
          </div>
        )}
      </div>
    );
  }

  if (registrationComplete) {
    return (
      <RegistrationComplete
        referralLink={referralLink}
        copySuccess={copySuccess}
        onCopyLink={handleCopyLink}
        htmlId={htmlId}
        className={className}
        ctaButtonText={ctaButtonText}
        ctaButtonLink={ctaButtonLink}
      />
    );
  }

  // Logged-in but NOT a travel agent — show profile review with edit toggle
  if (isUserSignedIn) {
    const userName = formData.firstName || window.user_data?.user_first_name || '';
    const userCountry = countryOptions.find(o => o.value === formData.country)?.label
      || window.user_data?.country_of_residence
      || '';

    // Read-only view matching the "Welcome, Joe" mockup
    if (!isEditMode) {
      return (
        <div
          id={htmlId}
          data-testid="ta-registration-form"
          className={`max-w-[1440px] mx-auto px-8 py-12 ${className}`}
        >
          <div className="mb-8">
            <h1
              className="text-[48px] font-semibold leading-[62px] text-black mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
              data-testid="ta-form-heading"
            >
              Welcome, {userName}
            </h1>
            <p
              className="text-[18px] font-normal leading-[23px] text-black"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Please review your profile information below and make any needed updates
            </p>
          </div>

          <div className="border border-gray-200 rounded-2xl p-8 max-w-[1152px]">
            <h2
              className="text-[20px] font-semibold leading-[26px] text-black mb-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Account information
            </h2>

            <AccountInfoReadOnly formData={formData} userCountry={userCountry} />

            <h2
              className="text-[20px] font-semibold leading-[26px] text-black mb-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Travel advisor information
            </h2>

            <AdvisorInfoReadOnly
              {...advisorReadOnlyProps}
              onEdit={() => setIsEditMode(true)}
            >
              {referralLink && (
                <ReferralSection
                  referralLink={referralLink}
                  copySuccess={copySuccess}
                  onCopyLink={handleCopyLink}
                />
              )}
            </AdvisorInfoReadOnly>
          </div>

          <MobileVerifyModal
            isOpen={isMobileVerifyOpen}
            onClose={handleMobileVerifyComplete}
            onVerified={handleMobileVerifyComplete}
            phoneNumber={formData.mobilePhone}
            authToken={verifyAuthToken}
            brandId={brandId}
          />
        </div>
      );
    }

    // Edit mode — full editable form
    return (
      <div
        id={htmlId}
        data-testid="ta-registration-form"
        className={`max-w-[1440px] mx-auto px-8 py-12 ${className}`}
      >
        <div className="mb-8">
          <h1
            className="text-[48px] font-semibold leading-[62px] text-black mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
            data-testid="ta-form-heading"
          >
            Welcome, {userName}
          </h1>
          <p
            className="text-[18px] font-normal leading-[23px] text-black"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Please review your profile information below and make any needed updates
          </p>
        </div>

        <form onSubmit={handleSubmit} onFocus={trackFormStarted} onClick={trackFormStarted} className="max-w-[1152px]">
          <div className="border border-gray-200 rounded-2xl p-8 mb-8">
            <h2
              className="text-[20px] font-semibold leading-[26px] text-black mb-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Account information
            </h2>

            <AccountInfoFields
              formData={formData}
              handleInputChange={handleInputChange}
              loading={loading}
              countryOptions={countryOptions}
              isLoadingCountries={isLoadingCountries}
            />

            <h2
              className="text-[20px] font-semibold leading-[26px] text-black mb-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Travel advisor information
            </h2>

            <div className="mb-6">
              <AdvisorInfoFields {...advisorFieldProps} />
            </div>
          </div>

          <TermsAndSubmit
            formData={formData}
            handleInputChange={handleInputChange}
            loading={loading}
            isExistingAdvisor={true}
          />
        </form>

        <MobileVerifyModal
          isOpen={isMobileVerifyOpen}
          onClose={handleMobileVerifyComplete}
          onVerified={handleMobileVerifyComplete}
          phoneNumber={formData.mobilePhone}
          authToken={verifyAuthToken}
          brandId={brandId}
        />
      </div>
    );
  }

  return (
    <div
      id={htmlId}
      data-testid="ta-registration-form"
      className={`max-w-[1440px] mx-auto px-8 py-12 ${className}`}
    >
      {/* Header Section */}
      <div className="mb-10">
        <h1
          className="text-[48px] font-semibold leading-[62px]
          text-black mb-4"
          style={{ fontFamily: 'Poppins, sans-serif' }}
          data-testid="ta-form-heading"
        >
          {isClaimFlow ? `Welcome, ${formData.firstName}` : 'Sign up.'}
        </h1>

        {/* Info Alert - hide for claim flow and signed-in users */}
        {showLoginLink && !isUserSignedIn && !isClaimFlow && (
          <div className="flex items-start gap-2 mb-6" data-testid="ta-login-prompt">
            <div className="flex items-center gap-2 bg-blue-50 px-4 py-3 rounded-lg">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                <circle cx="12" cy="12" r="9.5" stroke="#252525" strokeWidth="1.5"/>
                <path d="M12 11V16M12 8H12.01" stroke="#252525" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span className="text-[18px] font-normal
              leading-[23px] text-black" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Already have a Ship&Play, LuggageFree, or ShipSticks account?
              </span>
              <button
                type="button"
                onClick={() => setIsLoginModalOpen(true)}
                className="text-[18px] font-semibold leading-[23px] text-[#252525] underline
                  ml-2 whitespace-nowrap bg-transparent border-none cursor-pointer"
                style={{ fontFamily: 'Poppins, sans-serif' }}
                data-testid="ta-login-link"
              >
                Login here
              </button>
            </div>
          </div>
        )}

        {/* Description */}
        <p className="text-[18px] font-normal leading-[23px]
         text-black max-w-[1152px]" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Becoming a Ship&Play travel advisor is simple, fast, and
          completely free. We&apos;ll help you get set up,
          support your team, and provide the tools you need to
          deliver a better experience from the moment guests book
          their trip.
        </p>
        {isClaimFlow && (
          <p className="text-[18px] font-semibold leading-[23px]
           text-black mt-4 underline" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Simply create a password to sign up!
          </p>
        )}
      </div>

      {/* Form Section */}
      <form onSubmit={handleSubmit} onFocus={trackFormStarted} onClick={trackFormStarted} className="max-w-[1152px]">
        <div className="border border-gray-300 rounded-lg p-6 mb-8">
          <h2 className="text-[24px] font-semibold leading-[31px]
           text-black mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Account information
          </h2>

          {isClaimFlow && isClaimReadOnly ? (
            <AccountInfoReadOnly formData={formData} />
          ) : (
            <AccountInfoFields
              formData={formData}
              handleInputChange={handleInputChange}
              loading={loading}
              countryOptions={countryOptions}
              isLoadingCountries={isLoadingCountries}
              checkEmailExists={checkEmailExists}
              isClaimFlow={isClaimFlow}
            />
          )}

          <h2 className="text-[24px] font-semibold leading-[31px]
           text-black mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Travel advisor information
          </h2>

          {isClaimFlow && isClaimReadOnly ? (
            <AdvisorInfoReadOnly
              {...advisorReadOnlyProps}
              onEdit={() => setIsClaimReadOnly(false)}
            />
          ) : (
            <AdvisorInfoFields {...advisorFieldProps} />
          )}
        </div>

        <div className="border border-gray-300 rounded-lg p-6 mb-8">
          <h2 className="text-[24px] font-semibold leading-[31px]
           text-black mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Create Password
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <FormField
              label="Password"
              placeholder="Enter password"
              value={formData.password}
              onChange={(value) => {
                handleInputChange('password', value);
                if (passwordError) setPasswordError(validatePassword(value));
                if (confirmPasswordError)
                  setConfirmPasswordError(validateConfirmPassword(formData.confirmPassword, value));
              }}
              onBlur={() => setPasswordError(validatePassword(formData.password))}
              type="password"
              disabled={loading}
              error={passwordError}
            />

            <FormField
              label="Confirm password"
              placeholder="Confirm password"
              value={formData.confirmPassword}
              onChange={(value) => {
                handleInputChange('confirmPassword', value);
                if (confirmPasswordError) setConfirmPasswordError(validateConfirmPassword(value, formData.password));
              }}
              onBlur={() =>
                setConfirmPasswordError(validateConfirmPassword(formData.confirmPassword, formData.password))
              }
              type="password"
              disabled={loading}
              error={confirmPasswordError}
            />
          </div>
        </div>

        <TermsAndSubmit
          formData={formData}
          handleInputChange={handleInputChange}
          loading={loading}
          submitButtonText="Finish sign up"
        />
      </form>

      {/* Mobile Verify Modal */}
      <MobileVerifyModal
        isOpen={isMobileVerifyOpen}
        onClose={handleMobileVerifyComplete}
        onVerified={handleMobileVerifyComplete}
        phoneNumber={formData.mobilePhone}
        authToken={verifyAuthToken}
        brandId={brandId}
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
        sourceType="ta_login"
      />

      {/* Claim Welcome Modal */}
      {isClaimFlow && (
        <ClaimWelcomeModal
          isOpen={isClaimModalOpen}
          onClose={() => setIsClaimModalOpen(false)}
          advisorName={
            claimData?.advisor_name || formData.firstName
          }
          agencyName={claimData?.agency_name}
          consortiumName={claimData?.consortium_name}
          scrollToId={htmlId}
        />
      )}
    </div>
  );
};

TravelAdvisorSignUpForm.propTypes = {
  onSubmit: PropTypes.func,
  className: PropTypes.string,
  showLoginLink: PropTypes.bool,
  onLoginSuccess: PropTypes.func,
  htmlId: PropTypes.string,
  ctaButtonText: PropTypes.string,
  ctaButtonLink: PropTypes.string
};

export default TravelAdvisorSignUpForm;